import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useDocRow } from '@/lib/useDocs';
import { CompetencyPicker } from '@/components/CompetencyPicker';
import { messageForAuthError } from '@/lib/authErrors';
import { opportunityStatusLabel } from '@/lib/format';
import type { Competency, Opportunity, OpportunityTask } from '@/types/domain';
import { strings } from '@/strings';

const submitOpportunity = callable<{ opportunityId: string }, { opportunityId: string; status: string }>('submitOpportunity');

const MODES = [
  { value: 'remote', label: strings.modeRemote },
  { value: 'onsite', label: strings.modeOnsite },
  { value: 'hybrid', label: strings.modeHybrid },
  { value: 'field', label: strings.modeField },
] as const;

const DURATIONS = [
  { value: 'three_months', label: strings.durationThreeMonths },
  { value: 'six_months', label: strings.durationSixMonths },
  { value: 'one_year', label: strings.durationOneYear },
  { value: 'ongoing', label: strings.durationOngoing },
  { value: 'project_bound', label: strings.durationProjectBound },
] as const;

const FLEXIBILITIES = [
  { value: 'fixed', label: strings.flexFixed },
  { value: 'flexible', label: strings.flexFlexible },
  { value: 'by_tasks', label: strings.flexByTasks },
] as const;

/**
 * الحد الأدنى للخبرة. "أكثر من 5 سنوات" is stored as 6 — "at least six" — so the
 * threshold is a plain number the scorer can compare supervision years against
 * without interpreting a band.
 */
const MIN_YEARS = [
  { value: '', label: strings.oppMinYearsNone },
  { value: '2', label: `2 ${strings.unitYears}` },
  { value: '3', label: `3 ${strings.unitYears}` },
  { value: '5', label: `5 ${strings.unitYears}` },
  { value: '6', label: `${strings.unitYears} 6+` },
] as const;

const STYLES = [
  { value: 'individual', label: strings.oppStyleIndividual },
  { value: 'team', label: strings.oppStyleTeam },
];

const COMPENSATION = [
  { value: 'volunteer', label: strings.compVolunteer },
  { value: 'paid_part_time', label: strings.compPaidPartTime },
  { value: 'lump_sum', label: strings.compLumpSum },
  { value: 'consulting_contract', label: strings.compConsulting },
];

/**
 * The eight sections as one long form, saved as a draft by a direct Firestore
 * write — the one client write the rules permit, and only while the card is a
 * draft. Validation lives on the server: a builder that refuses to save an
 * incomplete card is a builder people abandon halfway through.
 */
export function OpportunityBuilderPage() {
  const { id = '' } = useParams();
  const { row, loading, error } = useDocRow<Opportunity>(id ? doc(db, 'opportunities', id) : null, [id]);

  const [form, setForm] = useState<Opportunity | null>(null);
  const [required, setRequired] = useState<Competency[]>([]);
  const [preferred, setPreferred] = useState<Competency[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (row && !form) {
      setForm(row);
      setRequired(row.required || []);
      setPreferred(row.preferred || []);
    }
  }, [row, form]);

  if (loading) return <p className="text-muted">{strings.loading}</p>;
  if (error || !row || !form) return <p role="alert" className="text-danger">{strings.loadFailed}</p>;

  const card = form;
  const editable = row.status === 'draft';

  function set<K extends keyof Opportunity>(key: K, value: Opportunity[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  function setTask(index: number, patch: Partial<OpportunityTask>) {
    const next = [...card.tasks];
    next[index] = { ...next[index], ...patch };
    set('tasks', next);
  }

  async function saveDraft() {
    await updateDoc(doc(db, 'opportunities', id), {
      // `status` is deliberately absent: the rules refuse a client write that
      // changes it, and publishing is reviewOpportunity's decision.
      summary: card.summary,
      tasks: card.tasks,
      workStyle: card.workStyle,
      modes: card.modes,
      location: card.location,
      required,
      preferred,
      hoursPerWeek: Number(card.hoursPerWeek) || 0,
      days: card.days,
      durationKind: card.durationKind,
      compensationType: card.compensationType,
      amount: Number(card.amount) || 0,
      paymentNotes: card.paymentNotes,
      reportsTo: card.reportsTo,
      successCriteria: card.successCriteria.filter(Boolean),
      reviewCadence: card.reviewCadence,
      orgId: card.orgId,
    });
  }

  async function act(fn: () => Promise<unknown>, ok: string) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    setMessage(null);
    try {
      await fn();
      setMessage(ok);
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-xl font-bold text-primary">{card.title}</h1>
        <span className="text-sm text-muted">{opportunityStatusLabel(row.status)}</span>
      </header>

      {row.reviewNote ? (
        <p className="rounded-md border border-warning bg-surface p-3 text-sm text-ink">{row.reviewNote}</p>
      ) : null}

      <label className="flex flex-col gap-1 text-sm" htmlFor="summary">
        {strings.oppSummary}
        <textarea id="summary" rows={2} disabled={!editable} value={card.summary}
          onChange={(e) => set('summary', e.target.value)}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface" />
      </label>

      <fieldset className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-white p-4">
        <legend className="px-1 text-sm font-semibold text-ink">{strings.oppTasks}</legend>
        {card.tasks.map((task, index) => (
          <div key={index} className="flex flex-wrap items-end gap-2">
            <label className="flex flex-1 flex-col gap-1 text-xs" htmlFor={`task-${index}`}>
              {strings.oppTask}
              <input id={`task-${index}`} aria-label={strings.oppTask} disabled={!editable}
                value={task.task} onChange={(e) => setTask(index, { task: e.target.value })}
                className="rounded-md border border-border-subtle px-2 py-1 text-sm" />
            </label>
            <label className="flex flex-1 flex-col gap-1 text-xs" htmlFor={`deliv-${index}`}>
              {strings.oppDeliverable}
              <input id={`deliv-${index}`} aria-label={strings.oppDeliverable} disabled={!editable}
                value={task.deliverable} onChange={(e) => setTask(index, { deliverable: e.target.value })}
                className="rounded-md border border-border-subtle px-2 py-1 text-sm" />
            </label>
            {editable ? (
              <button type="button" className="min-h-11 self-start text-xs text-danger"
                onClick={() => set('tasks', card.tasks.filter((_, i) => i !== index))}>
                {strings.oppRemoveTask}
              </button>
            ) : null}
          </div>
        ))}
        {editable ? (
          <button type="button" className="self-start min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => set('tasks', [...card.tasks, { task: '', deliverable: '' }])}>
            {strings.oppAddTask}
          </button>
        ) : null}
      </fieldset>

      <label className="flex flex-col gap-1 text-sm" htmlFor="workStyle">
        {strings.oppWorkStyle}
        <select id="workStyle" disabled={!editable} value={card.workStyle}
          onChange={(e) => set('workStyle', e.target.value as Opportunity['workStyle'])}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface">
          {STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </label>

      <fieldset className="flex flex-col gap-1 text-sm">
        <legend className="mb-1">{strings.fieldModes}</legend>
        {MODES.map((m) => (
          <label key={m.value} className="flex min-h-11 items-center gap-2">
            <input
              type="checkbox"
              disabled={!editable}
              checked={(card.modes || []).includes(m.value)}
              onChange={(e) => set('modes', e.target.checked
                ? [...(card.modes || []), m.value]
                : (card.modes || []).filter((x) => x !== m.value))}
            />
            {m.label}
          </label>
        ))}
      </fieldset>

      {/* Somewhere to be, unless the work is remote-ONLY. `modes` is a
          multi-select now, so a card that is remote *and* onsite still needs a
          location. */}
      {!((card.modes || []).length > 0 && (card.modes || []).every((m) => m === 'remote')) ? (
        <label className="flex flex-col gap-1 text-sm" htmlFor="location">
          {strings.oppLocation}
          <input id="location" disabled={!editable} value={card.location}
            onChange={(e) => set('location', e.target.value)}
            className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface" />
        </label>
      ) : null}

      <div className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
        <span className="text-sm font-semibold text-ink">{strings.oppRequired}</span>
        <p className="text-xs text-muted">{strings.oppRequirementsHint}</p>
        <CompetencyPicker value={required} onChange={setRequired} />
        <span className="text-sm font-semibold text-ink">{strings.oppPreferred}</span>
        <CompetencyPicker value={preferred} onChange={setPreferred} />
      </div>

      <label className="flex flex-col gap-1 text-sm" htmlFor="hours">
        {strings.fieldHours}
        <input id="hours" type="number" min={0} disabled={!editable} value={card.hoursPerWeek}
          onChange={(e) => set('hoursPerWeek', Number(e.target.value))}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface" />
      </label>

      {/* Two of the card's five options — مستمرة and مرتبطة بمشروع — are not a
          number of months, so this cannot be a number input. */}
      <label className="flex flex-col gap-1 text-sm" htmlFor="duration">
        {strings.oppDurationKind}
        <select id="duration" disabled={!editable} value={card.durationKind}
          onChange={(e) => set('durationKind', e.target.value as Opportunity['durationKind'])}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface">
          {DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm" htmlFor="minYears">
        {strings.oppMinYears}
        <select id="minYears" disabled={!editable} value={card.minYears === null ? '' : String(card.minYears)}
          onChange={(e) => set('minYears', e.target.value === '' ? null : Number(e.target.value))}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface">
          {MIN_YEARS.map((y) => <option key={y.label} value={y.value}>{y.label}</option>)}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm" htmlFor="flexibility">
        {strings.oppFlexibility}
        <select id="flexibility" disabled={!editable} value={card.flexibility}
          onChange={(e) => set('flexibility', e.target.value as Opportunity['flexibility'])}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface">
          {FLEXIBILITIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm" htmlFor="compensation">
        {strings.oppCompensation}
        <select id="compensation" disabled={!editable} value={card.compensationType}
          onChange={(e) => set('compensationType', e.target.value as Opportunity['compensationType'])}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface">
          {COMPENSATION.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </label>

      {card.compensationType !== 'volunteer' ? (
        <label className="flex flex-col gap-1 text-sm" htmlFor="amount">
          {strings.oppAmount}
          <input id="amount" type="number" min={0} disabled={!editable} value={card.amount}
            onChange={(e) => set('amount', Number(e.target.value))}
            className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface" />
        </label>
      ) : null}

      <label className="flex flex-col gap-1 text-sm" htmlFor="reportsTo">
        {strings.oppReportsTo}
        <input id="reportsTo" disabled={!editable} value={card.reportsTo}
          onChange={(e) => set('reportsTo', e.target.value)}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface" />
      </label>

      <label className="flex flex-col gap-1 text-sm" htmlFor="success">
        {strings.oppSuccess}
        <textarea id="success" rows={2} disabled={!editable} value={card.successCriteria.join('\n')}
          onChange={(e) => set('successCriteria', e.target.value.split('\n'))}
          className="rounded-md border border-border-subtle px-3 py-2 disabled:bg-surface" />
      </label>

      <div className="flex flex-wrap items-center gap-3">
        {editable ? (
          <>
            <button type="button" disabled={busy} onClick={() => act(saveDraft, strings.candidateSaved)}
              className="rounded-md border border-primary px-4 py-2 text-primary disabled:opacity-50">
              {strings.oppSaveDraft}
            </button>
            <button type="button" disabled={busy}
              onClick={() => act(async () => {
                await saveDraft();
                await submitOpportunity({ opportunityId: id });
              }, strings.oppSubmitted)}
              className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
              {strings.oppSubmit}
            </button>
          </>
        ) : null}
        {message ? <span className="text-sm text-success">{message}</span> : null}
        {problem ? <span role="alert" className="text-sm text-danger">{problem}</span> : null}
      </div>
    </div>
  );
}
